import { useEffect, useState } from "react";
import { FaExternalLinkAlt, FaExclamationTriangle } from "react-icons/fa";
import { RxCross1 } from "react-icons/rx";

type IframeViewerProps = {
  url: string;
  onClose?: () => void;
  width?: string;
};

const IframeViewer = ({ url, onClose,width }: IframeViewerProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isProxyMode, setIsProxyMode] = useState(false);
  const [popupWindow, setPopupWindow] = useState<Window | null>(null);

  useEffect(() => {
    let cancelled = false;

    setIsLoaded(false);
    setHasError(false);

    checkIframeBlocked(url).then((blocked) => {
      if (!cancelled) {
        if (blocked) {
             const popup = window.open(url, "myPopup", "width=800,height=600");
          if (popup) {
            setPopupWindow(popup);
          }
          setHasError(true);
        }
        setIsLoaded(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [url]);

  const proxyServices = [
    `https://corsproxy.io/?${encodeURIComponent(url)}`,
    `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`,
    `https://cors-anywhere.herokuapp.com/${url}`,
  ];

  const openInSidebarPopup = () => {
    const screenWidth = window.screen.availWidth;
    const screenHeight = window.screen.availHeight;
    const popupWidth = Math.min(800, screenWidth * 0.4);
    const popupHeight = screenHeight - 100;
    const left = screenWidth - popupWidth - 50;
    const top = 50;

    const popup = window.open(
      url,
      'sidebar_popup',
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,resizable=yes,toolbar=yes,menubar=no,location=yes,status=no`
    );

    if (popup) {
      setPopupWindow(popup);
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          setPopupWindow(null);
          clearInterval(checkClosed);
        }
      }, 1000);
      popup.focus();
    } else {
      alert('Popup blocked! Please allow popups for this site.');
    }
  };

  const tryProxyMode = (proxyIndex = 0) => {
    if (proxyIndex < proxyServices.length) {
      setIsProxyMode(true);
      setHasError(false);
      setIsLoaded(false);

      const testIframe = document.createElement('iframe');
      testIframe.style.display = 'none';
      testIframe.src = proxyServices[proxyIndex];

      testIframe.onload = () => {
        setIsLoaded(true);
        document.body.removeChild(testIframe);
      };

      testIframe.onerror = () => {
        document.body.removeChild(testIframe);
        tryProxyMode(proxyIndex + 1);
      };

      document.body.appendChild(testIframe);
    } else {
      setHasError(true);
      setIsLoaded(true);
    }
  };

  const currentUrl = isProxyMode ? proxyServices[0] : url;
  const shouldShowIframe = !hasError;

  return (
    <div 
     className={`fixed top-15 h-screen bg-[#FDFDFD] shadow-xl border border-gray-200 z-50 flex flex-col  ${
      width ? width : "right-0 w-[30%] 2xl:w-[25%]"
    }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between bg-[#FDFDFD] p-2 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 flex items-center border border-gray-300 rounded-xl hover:bg-gray-50 hover:text-black text-xs p-2 cursor-pointer transition-colors"
          >
            Open
            <FaExternalLinkAlt className="ml-1 inline-block" />
          </a>

          {hasError && (
            <button
              onClick={openInSidebarPopup}
              className="text-blue-600 flex items-center border border-blue-300 rounded-xl hover:bg-blue-50 text-xs p-2 cursor-pointer transition-colors"
            >
              Open Sidebar
            </button>
          )}

          {hasError && !isProxyMode && (
            <button
              onClick={() => tryProxyMode()}
              className="text-orange-600 flex items-center border border-orange-300 rounded-xl hover:bg-orange-50 text-xs p-2 cursor-pointer transition-colors"
            >
              Try Proxy
            </button>
          )}
        </div>

        <button
          onClick={onClose}
          className="text-gray-400 rounded-full hover:bg-gray-50 hover:text-black text-xl font-bold p-2 cursor-pointer transition-colors"
        >
          <RxCross1 className="h-5 w-5 font-extrabold" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 relative overflow-hidden bg-[#FDFDFD] px-2 rounded-b-2xl">
        {!isLoaded && !hasError && (
          <div className="w-full h-full animate-pulse bg-white rounded-xl overflow-hidden border border-gray-200">
            <div className="flex items-center space-x-2 p-3 bg-gray-100 border-b border-gray-200">
              <div className="w-3 h-3 rounded-full bg-gray-400"></div>
              <div className="w-3 h-3 rounded-full bg-gray-400"></div>
              <div className="w-3 h-3 rounded-full bg-gray-400"></div>
              <div className="ml-4 flex-1 h-4 bg-gray-200 rounded"></div>
            </div>
            <div className="p-4 space-y-4">
              <div className="h-40 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        )}

        {hasError && (
          <div className="w-full h-full flex items-center justify-center bg-white rounded-xl border border-gray-200">
            <div className="text-center p-8 max-w-sm">
              <FaExclamationTriangle className="mx-auto text-4xl text-orange-500 mb-4" />
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Website Blocks Embedding
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                This website prevents iframe embedding (X-Frame-Options/CSP).
              </p>
              <div className="space-y-2">
                <button
                  onClick={openInSidebarPopup}
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {popupWindow && !popupWindow.closed ? 'Focus Sidebar Window' : 'Open in Sidebar'}
                </button>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors text-center"
                >
                  Open in New Tab
                </a>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(url);
                    alert('URL copied to clipboard!');
                  }}
                  className="block w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Copy URL
                </button>
              </div>
            </div>
          </div>
        )}

        {shouldShowIframe && !hasError && (
          <>
            {isProxyMode && (
              <div className="absolute top-2 left-2 z-10 bg-orange-100 border border-orange-300 rounded-lg px-2 py-1 text-xs text-orange-700">
                Using Proxy Service
              </div>
            )}
            <iframe
              src={currentUrl}
              title="Document Viewer"
              className="w-full h-full bg-white rounded-xl border-0"
              // sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
              allow="accelerometer; autoplay; clipboard-read; clipboard-write; encrypted-media; fullscreen; geolocation; microphone; "
            />
          </>
        )}
      </div>
    </div>
  );
};

// 🔹 Dynamic check for iframe embedding restrictions
const checkIframeBlocked = (testUrl: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = testUrl;

    const cleanup = () => {
      if (iframe.parentNode) document.body.removeChild(iframe);
    };

    iframe.onload = () => {
    
        cleanup();
        resolve(false); // Not blocked
     
    };

    iframe.onerror = () => {
      cleanup();
      resolve(true);
    };

    document.body.appendChild(iframe);
  });
};

export default IframeViewer;
